import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { apiService } from '../utils/api';
import { useSelector } from 'react-redux';

interface NavigationPage {
  next: number | null;
  results: unknown[];
}

interface ContractState {
  contract: { id: string; title: string } | null;
  modelId: string | null;
}

interface TypeMarkState {
  menuId: string | null;
}

interface RootState {
  contract: ContractState;
  typeMark: TypeMarkState;
}

interface NavigationResult {
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  navigationItems: unknown[];
  pages: NavigationPage[];
}

export interface NavigationOptions {
  apiName?: string;
  extraParams?: Record<string, unknown>;
}

const fetchData = async (
  apiName: string,
  page: number,
  search: string,
  filter: Record<string, unknown>,
  contractId: string | null,
  modelId: string | null,
  menuId: string | null,
  classification: string | undefined,
  extraParams: Record<string, unknown> = {}
): Promise<NavigationPage | undefined> => {
  try {
    // A `model` set by a panel filter overrides the globally selected model.
    const { model: filterModel, ...restFilter } = filter;
    const { data } = await apiService.get(`/api/v1/${apiName}/left-panel/`, {
      params: {
        page: page,
        page_size: 30,
        search: search,
        is_active: [true],
        ...restFilter,
        contract: contractId,
        model: filterModel ?? modelId,
        menu: menuId,
        classification: classification,
        ...extraParams,
      },
    });
    return data;
  } catch (_e) {
    //
  }
};

const useGetNavigation = (
  queryKey: string,
  search: string,
  filter: Record<string, unknown> = {},
  classification?: string,
  options: NavigationOptions = {}
): NavigationResult => {
  const { apiName = queryKey, extraParams } = options;
  const { contract, modelId } = useSelector((state: RootState) => state.contract);
  const contractId = contract?.id ?? null;
  const { menuId } = useSelector((state: RootState) => state.typeMark);

  const { fetchNextPage, hasNextPage, isError, isFetching, isLoading, data } = useInfiniteQuery({
    queryKey: [queryKey, search, filter, menuId, contractId, modelId, classification, extraParams],
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => {
      if (!pageParam) {
        return;
      }

      return fetchData(
        apiName,
        pageParam,
        search,
        filter,
        contractId,
        modelId,
        menuId,
        classification,
        extraParams
      );
    },
    getNextPageParam: (lastPage) => lastPage?.next,
    refetchOnWindowFocus: false,
  });

  const pages = useMemo(() => data?.pages.filter(Boolean) || [], [data?.pages]);
  const navigationItems = useMemo(() => pages.flatMap((page) => page.results) || [], [pages]);

  return useMemo(
    () => ({
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching,
      isLoading,
      navigationItems,
      pages,
    }),
    [
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching,
      isLoading,
      navigationItems,
      pages,
      search,
      filter,
      menuId,
    ]
  );
};

export { useGetNavigation };
